import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { Button } from "~/components/ui";
import { buildLocalizedPath, type Lang } from "~/config/route-config";

import { ErrorSurface } from "./ErrorSurface";

/** Minimum perceptible breathing before the retry hands control back (design contract). */
const PENDING_MS = 700;
/** If the reload has not taken the page away by now, restore the CTA in its "try once more" state. */
const RESTORE_MS = 1800;
/** Exponential backoff added on top of `PENDING_MS` for each successive reload attempt: 1st immediate, 2nd +2s, 3rd +5s. */
const RETRY_BACKOFF_MS = [0, 2000, 5000] as const;
/** After this many reload attempts have failed to take the page away, stop reloading and point to support instead. */
const MAX_RETRY_ATTEMPTS = RETRY_BACKOFF_MS.length;
/** How long a spent retry budget is remembered. Past this, the counter reads as fresh. */
const RETRY_MEMORY_MS = 10 * 60 * 1000;
const RETRY_STORAGE_PREFIX = "tfc:e500:retry:";

/**
 * Per-URL: a 500 on one route says nothing about the retry budget of another.
 *
 * Client-only by construction — every caller is a click handler or an effect.
 * The guard is explicit rather than left to the `try/catch` below so that a
 * future server-side caller reads as "no budget" on purpose, instead of a
 * `window is not defined` swallowed into a silent 0.
 */
function retryStorageKey(): string | null {
  if (typeof window === "undefined") return null;
  return `${RETRY_STORAGE_PREFIX}${window.location.pathname}`;
}

/**
 * The attempt counter has to outlive `window.location.reload()`.
 *
 * A reload that succeeds in re-requesting the page destroys this JS context, so
 * React state alone resets to zero on every attempt — the backoff would never
 * escalate past the first step and `exhausted` would be unreachable in exactly
 * the case it exists for (a 500 that keeps happening). `sessionStorage` is
 * per-tab and dies with it, which is the right lifetime for "how many times has
 * this page just failed on me".
 */
function readAttempt(): number {
  const key = retryStorageKey();
  if (key === null) return 0;
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return 0;
    const parsed = JSON.parse(raw) as { attempt?: unknown; ts?: unknown };
    if (typeof parsed.attempt !== "number" || typeof parsed.ts !== "number") return 0;
    if (Date.now() - parsed.ts > RETRY_MEMORY_MS) return 0;
    return parsed.attempt;
  } catch {
    // Safari private mode throws on storage access, and a hand-edited value is
    // not worth crashing an error page over. Degrade to "first try".
    return 0;
  }
}

function writeAttempt(attempt: number) {
  const key = retryStorageKey();
  if (key === null) return;
  try {
    window.sessionStorage.setItem(key, JSON.stringify({ attempt, ts: Date.now() }));
  } catch {
    // Losing the counter degrades to the previous behaviour (retry forever)
    // rather than breaking the one surface she sees when everything else broke.
  }
}

export interface ErrorPage500Props {
  /**
   * Correlates the surface with server-side logs. **Generated and propagated by
   * T38** (from `X-Request-Id` / `cf-ray`); this component only renders it.
   * Format `XXXX-XXXX-XXXX`, alphabet `ABCDEFGHJKLMNPQRSTUVWXYZ23456789`.
   */
  incidentId?: string;
  /** Pre-formatted `YYYY-MM-DD · HH:MM UTC`. Also supplied by T38. */
  timestampUtc?: string;
  /** See `ErrorSurfaceProps.standalone` — true in the root boundary. */
  standalone?: boolean;
}

/**
 * 500 — something failed on our side, unrecoverable in-request.
 *
 * Deliberately does NOT generate `incidentId` or `timestampUtc`: both are T38's
 * contract, and inventing them client-side would render a value that correlates
 * with nothing (and would desync SSR from hydration). Absent props → the
 * incident rows are omitted rather than faked.
 *
 * The exponential-backoff strategy (1st immediate · 2nd +2s · 3rd +5s · 4th show
 * contact) ships here. The attempt counter lives in `sessionStorage`, not in
 * React state, because the reload it counts destroys this JS context — see
 * `readAttempt`.
 */
export function ErrorPage500({
  incidentId,
  timestampUtc,
  standalone = false,
}: ErrorPage500Props) {
  const { t, i18n } = useTranslation("error");
  const lang: Lang = i18n.language === "en" ? "en" : "es";

  // The CTA is always in exactly one of these, and the label/target follows
  // from it. "exhausted" is terminal — reached once `attempt` hits
  // MAX_RETRY_ATTEMPTS, it never reverts to "idle".
  const [retry, setRetry] = useState<"idle" | "pending" | "attempted" | "exhausted">(
    "idle",
  );
  const [attempt, setAttempt] = useState(0);
  const pending = retry === "pending";
  const exhausted = retry === "exhausted";

  const reloadTimer = useRef<number | undefined>(undefined);
  const restoreTimer = useRef<number | undefined>(undefined);
  const contactRef = useRef<HTMLAnchorElement>(null);
  /** Set only when a click exhausted the budget, so the focus move below never
   * fires on a cold mount that merely read a spent counter out of storage. */
  const exhaustedByClick = useRef(false);

  // Restore the budget spent before the last reload. In an effect, not in the
  // useState initialiser: this component server-renders, so reading storage
  // during render would throw on the server and tear hydration on the client.
  //
  // Accepted cost: someone arriving with a spent budget sees the retry CTA for
  // one paint before it swaps to the contact link. Gating the render on a
  // "loading" sentinel would remove the flash and cost more than it buys — the
  // server would emit no primary CTA at all, which is a real regression for a
  // no-JS visitor on the one page that exists for when things are broken.
  useEffect(() => {
    const spent = readAttempt();
    if (spent <= 0) return;
    setAttempt(spent);
    setRetry(spent >= MAX_RETRY_ATTEMPTS ? "exhausted" : "attempted");
  }, []);

  // A pending reload must not outlive this component. The secondary CTA is a
  // client-side <Link>, so an armed timer would reload whatever page she
  // navigated to — and the backoff widens that window from 700ms to ~5.7s.
  useEffect(
    () => () => {
      window.clearTimeout(reloadTimer.current);
      window.clearTimeout(restoreTimer.current);
    },
    [],
  );

  // The CTA changes identity under her cursor 1.8s after a click. Move focus
  // with it, so keyboard and screen-reader users are told rather than dropped
  // onto <body> with the button they were on now gone.
  useEffect(() => {
    if (!exhausted || !exhaustedByClick.current) return;
    exhaustedByClick.current = false;
    contactRef.current?.focus();
  }, [exhausted]);

  function onRetry() {
    if (pending || exhausted) return;
    const nextAttempt = attempt + 1;
    const delay = PENDING_MS + (RETRY_BACKOFF_MS[attempt] ?? 0);
    // Persist BEFORE the reload fires: anything written after it is written to
    // a document that is already on its way out.
    writeAttempt(nextAttempt);
    setRetry("pending");
    reloadTimer.current = window.setTimeout(() => {
      window.location.reload();
      // The reload normally unloads this page. If it has not (blocked, or the
      // same error re-renders in place), hand control back rather than leaving
      // the CTA stuck in its pending state.
      restoreTimer.current = window.setTimeout(() => {
        setAttempt(nextAttempt);
        if (nextAttempt >= MAX_RETRY_ATTEMPTS) {
          exhaustedByClick.current = true;
          setRetry("exhausted");
        } else {
          setRetry("attempted");
        }
      }, RESTORE_MS);
    }, delay);
  }

  const primaryLabel = t(
    retry === "pending"
      ? "server.primaryPending"
      : retry === "attempted"
        ? "server.primaryRetry"
        : "server.primary",
  );

  return (
    <ErrorSurface
      standalone={standalone}
      mark={<span className="es-glyph">500</span>}
      markWarn
      eyebrow={
        <div className="af-eyebrow-row">
          <span>{t("server.eyebrowLabel")}</span>
          <span className="meta">{t("server.eyebrowMeta")}</span>
        </div>
      }
      title={t("server.title")}
      sub={t("server.sub")}
      primary={
        exhausted ? (
          // An <a>, not a handler: middle-click and open-in-new-tab must work,
          // same as ErrorPage404's contact link.
          <Button
            ref={contactRef}
            href={buildLocalizedPath("support", lang)}
            variant="dark" className="w-full"
          >
            <span>{t("server.primaryContact")}</span>
            <span className="af-submit-arrow" aria-hidden="true">
              →
            </span>
          </Button>
        ) : (
          <Button
            variant="dark"
            className="w-full"
          isLoading={pending}
            onClick={onRetry}
          >
            <span>{primaryLabel}</span>
            {(
              <span className="af-submit-arrow" aria-hidden="true">
                →
              </span>
            )}
          </Button>
        )
      }
      secondary={
        <Link to={buildLocalizedPath("home", lang)} className="af-link">
          {t("server.secondary")}
        </Link>
      }
      footer={
        <>
          <div className="row">
            {incidentId ? (
              <>
                <span>{t("server.incidentLabel")}</span>
                <span className="sep" aria-hidden="true" />
                <span className="es-incident">{incidentId}</span>
                <span className="sep" aria-hidden="true" />
              </>
            ) : null}
            <Link to={buildLocalizedPath("support", lang)}>{t("server.report")}</Link>
          </div>
          {timestampUtc ? (
            <div className="row">
              <span>{timestampUtc}</span>
            </div>
          ) : null}
        </>
      }
    />
  );
}
